import React, { useState, useCallback, useEffect, useMemo, useRef, lazy, Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import useHomeSearch from '../hooks/useHomeSearch';
import useHomeDashboard from '../hooks/useHomeDashboard';
import useHomeLocation from '../hooks/useHomeLocation';
import { unionFeedKeys, feedItemKey } from '../utils/feedItemKey';
import { activityPostFoundVia } from '../utils/foundVia';
import { buildPlans, happeningPlaceAt, tripCover } from '../utils/homePlans';
import { FALLBACK_LOCAL_RADIUS_KM } from '../config/whatsOnDiscovery';
import { inviteCover, respondToInvite, toModalInvitation } from '../utils/pendingInvites';
import { getLocalDateString } from '../utils/dateUtils';
import { shortPlace } from '../utils/tripAwareLabels';
import useFeedModals from '../hooks/useFeedModals';
import useDiscoverCardActions from '../hooks/useDiscoverCardActions';
import usePageTitle from '../hooks/usePageTitle';
import PageLayout from '../components/shared/PageLayout';
import OnboardingActivation from '../components/OnboardingActivation';
import PoweredByGoogle from '../components/shared/PoweredByGoogle';
import ConfirmDialog from '../components/ConfirmDialog';
import InvitationResponseModal from '../components/InvitationResponseModal';
import HomeHero from '../components/Dashboard/HomeHero';
import PlansRail from '../components/Dashboard/PlansRail';
import HomeSearch from '../components/Dashboard/HomeSearch';
import ThisWeekendSection, { weekendShown, weekendSlice } from '../components/Dashboard/ThisWeekendSection';
import WhileAwaySection from '../components/Dashboard/WhileAwaySection';
import TravelInspirationSection from '../components/Dashboard/TravelInspirationSection';
import NewPlacesSection from '../components/Dashboard/NewPlacesSection';
import LocationNudgeCard from '../components/Dashboard/LocationNudgeCard';

const CommentsModal = lazy(() => import('../components/CommentsModal'));
const UnifiedSaveModal = lazy(() => import('../components/UnifiedSaveModal'));
const ScheduleActivityModal = lazy(() => import('../components/ScheduleActivityModal'));
const ActivityInviteModal = lazy(() => import('../components/ActivityInviteModal'));
const NotInterestedModal = lazy(() => import('../components/NotInterestedModal'));
const SharingModal = lazy(() => import('../components/SharingModal'));
const ReportModal = lazy(() => import('../components/ReportModal'));

// Google Maps badge for the lists that can show Google Places content (docs/
// GOOGLE_ATTRIBUTION.md rule 2): each list is its own container mid-page, so
// each carries its own badge; a page-footer badge is too far away to count.
const googleBadge = <PoweredByGoogle variant="slim" align="end" className="-mt-2" />;

const scrollTo = (ref) => ref.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });

/**
 * Home (the refresh of 2026-11-01, Direction D of the Home page mockups):
 *
 *   hero            the one trip the ladder picks, or the home city this weekend
 *   rail            every other plan, one chip each
 *   search          one quiet pill that opens the full search bar
 *   This weekend    what's on near home — dated things only
 *   While you're in things to do at the destinations you've planned
 *   Travel inspiration  people's trips, friends and strangers in one grid
 *   New places      undated places near home that aren't on your lists yet
 *
 * The rule the page is built on: the top never depends on how much the user
 * has planned (one trip left the old Coming Up strip mostly blank; none left
 * an empty-state box). The lists keep the Discovery Lists model's boundary
 * (docs/DISCOVERY_LISTS_MODEL.md §3): if it has a date you could miss it is
 * This weekend's, nowhere else.
 */
function Home() {
  const { t } = useTranslation('home');
  const search = useHomeSearch();
  const dashboard = useHomeDashboard();
  const { home, coordinates: homeCoords } = useHomeLocation();
  const homeCity = shortPlace(home);
  usePageTitle(t('pageTitle'));

  // The viewer's plans, read once per change: hero, rail, "While you're in".
  const plans = useMemo(
    () => buildPlans({ trips: dashboard.plans.trips, invites: dashboard.plans.invites, activities: dashboard.plans.activities }),
    [dashboard.plans.trips, dashboard.plans.invites, dashboard.plans.activities]
  );
  const totalPlans = plans.rail.length + (plans.hero.kind === 'home' ? 0 : 1);

  // With nothing planned the hero shows the home city this weekend, so it
  // wants the weekend before that section scrolls into view.
  const { loadWhatsOn } = dashboard;
  const heroWantsWeekend = !dashboard.plans.loading && plans.hero.kind === 'home';
  useEffect(() => {
    if (heroWantsWeekend && !dashboard.whatsOn.loaded && !dashboard.whatsOn.loading) loadWhatsOn();
  }, [heroWantsWeekend, dashboard.whatsOn.loaded, dashboard.whatsOn.loading, loadWhatsOn]);

  // Jump targets for the hero's buttons.
  const searchRef = useRef(null);
  const weekendRef = useRef(null);
  const awayRef = useRef(null);
  const [searchOpen, setSearchOpen] = useState(false);
  // The hero's "Ideas for day N" names its trip; the section selects it
  // (a nonce per jump, so the same trip can be jumped to twice).
  const [awayFocus, setAwayFocus] = useState(null);
  const jumpToAway = useCallback((tripId) => {
    setAwayFocus({ id: tripId, nonce: Date.now() });
    scrollTo(awayRef);
  }, []);
  const openSearch = useCallback(() => {
    setSearchOpen(true);
    // After the bar mounts, so the scroll lands on the open form.
    requestAnimationFrame(() => scrollTo(searchRef));
  }, []);

  // Answering an invitation from the hero or the rail: the same modal and
  // response path as the planner's Invitations strip (per-child answers,
  // "already answered" resync via the invites-changed signal).
  const [openInvite, setOpenInvite] = useState(null);
  const liveInvite = openInvite
    ? dashboard.plans.invites.find(i => i.type === openInvite.type && i.id === openInvite.id) || openInvite
    : null;
  const respond = async (response, replyMessage, childId = null) => {
    await respondToInvite(liveInvite, response, replyMessage, childId);
  };

  // Track dismissed items (blocked/reported) so they disappear from all dashboard sections
  const [dismissedIds, setDismissedIds] = useState(new Set());

  // Cross-section dedupe, a PRIORITY LADDER (docs/DISCOVERY_LISTS_MODEL.md §3):
  //
  //     This weekend  >  Travel inspiration (people)  >  New places
  //
  // Scarcity is the reason: New places draws from the whole public table and
  // can spare a card; the weekend's dated things and people's posts cannot.
  // Travel inspiration folds the friends list in and dedupes inside itself.
  const whatsOnKeys = useMemo(
    () => unionFeedKeys(null, dashboard.whatsOn.items),
    [dashboard.whatsOn.items]
  );
  const beforeNewPlacesKeys = useMemo(
    () => unionFeedKeys(unionFeedKeys(whatsOnKeys, dashboard.friends.items), dashboard.inspiration.items),
    [whatsOnKeys, dashboard.friends.items, dashboard.inspiration.items]
  );
  const dismissItem = useCallback((itemId) => {
    setDismissedIds(prev => new Set([...prev, itemId]));
  }, []);

  // Feed modals shared across all feed sections.
  // When useFeedModals calls setFeedItems with a filter (block/report), we detect
  // which items were removed and add them to dismissedIds so the sections hide them.
  //
  // setFeedItems must be referentially stable: useFeedModals memoizes its
  // handlers (and the `modals` object) on it, and every card's action set
  // hangs off `modals`. Section items are read through a ref so the callback
  // needs no data deps.
  const sectionItemsRef = useRef([]);
  sectionItemsRef.current = [
    ...(dashboard.whatsOn.items || []),
    ...(dashboard.whatsAround.items || []),
    ...(dashboard.friends.items || []),
    ...(dashboard.inspiration.items || [])
  ];
  const { updateSectionItems } = dashboard;
  const setFeedItems = useCallback((updater) => {
    if (typeof updater !== 'function') return;
    const allItems = sectionItemsRef.current;
    const result = updater(allItems);
    if (result.length < allItems.length) {
      // Dismissal — track removed IDs
      const resultIds = new Set(result.map(i => i.id || i.content_id));
      allItems.forEach(item => {
        const id = item.id || item.content_id;
        if (resultIds.has(id)) return;
        // Events dismiss by canonical event/group key so a follow copy and a
        // ranked copy of one run go together. Everything else stays on its
        // own id: a place-backed key would hide other people's visits there.
        const key = feedItemKey(item);
        dismissItem(key && /^(event|group):/.test(key) ? key : id);
      });
    } else {
      // In-place update (like toggle, etc.) — propagate to all sections
      updateSectionItems(updater);
    }
  }, [dismissItem, updateSectionItems]);
  const modals = useFeedModals({ setFeedItems });
  const buildActions = useDiscoverCardActions(modals);

  // What the no-trip hero shows of the weekend: the same slice the section
  // renders (dismissals out, fenced to the weekend the title names, a show
  // back home below the ones where the phone is).
  const weekend = useMemo(
    () => weekendSlice(dashboard.whatsOn.items, dismissedIds, getLocalDateString(new Date()), dashboard.whatsOn.centerKind !== 'none',
      dashboard.whatsOn.awayFromHome ? dashboard.whatsOn.center : null),
    [dashboard.whatsOn.items, dashboard.whatsOn.centerKind, dashboard.whatsOn.awayFromHome, dashboard.whatsOn.center, dismissedIds]
  );

  // Where This weekend's list is fenced: home, or — with the phone away from
  // home — the trip under way there, by name ("This week in Barcelona", not
  // "in Antwerp" over Barcelona's events). Null says "near you".
  const weekendAway = !!dashboard.whatsOn.awayFromHome;
  const weekendPlace = weekendAway
    ? happeningPlaceAt(plans.away, dashboard.whatsOn.center, FALLBACK_LOCAL_RADIUS_KM)
    : homeCity;
  // Away, the weekend list and "While you're in" can be about the same city;
  // what the weekend already shows isn't offered again below it.
  const weekendShownKeys = useMemo(() => {
    if (!weekendAway) return null;
    const { lead, rows } = weekendShown(weekend.items);
    return unionFeedKeys(null, lead ? [lead, ...rows] : rows);
  }, [weekendAway, weekend.items]);

  // A trip's or an invitation's cover comes through the cover cascade, which
  // can be a Google photo: the badge sits under the rail, the next container.
  const heroPhotoIsCascade = !!((plans.hero.trip && tripCover(plans.hero.trip)) || (plans.hero.invite && inviteCover(plans.hero.invite)));

  return (
    <div className="min-h-dvh bg-background">
      {/* The hero: full-bleed, outside the page container. Trip covers can
          fall back to Google place photos (cover cascade), hence the badge
          under the rail. */}
      <HomeHero
        hero={plans.hero}
        loading={dashboard.plans.loading}
        error={dashboard.plans.error}
        onRetry={dashboard.retryPlans}
        homeCity={homeCity}
        homeCoords={homeCoords}
        weekendPlace={weekendPlace}
        weekendAway={weekendAway}
        weekendItems={weekend.items}
        weekendCount={weekend.items.filter(i => !i._awayFromCenter).length}
        weekendScope={weekend.scope}
        onSearch={openSearch}
        onOpenInvite={setOpenInvite}
        onJumpToAway={jumpToAway}
        onJumpToWeekend={() => scrollTo(weekendRef)}
      />

      <PageLayout size="wide" py="py-5 md:py-6" as="div" containerClassName="space-y-8 md:space-y-10" className="min-h-0">
        <div className="space-y-3">
          <PlansRail rail={plans.rail} totalPlans={totalPlans} onOpenInvite={setOpenInvite} />
          {heroPhotoIsCascade && googleBadge}
        </div>

        <HomeSearch ref={searchRef} search={search} open={searchOpen} onOpenChange={setSearchOpen} />

        {/* Activation bands (moved here from the profile 2026-09-13). Renders
            nothing once onboarding is done. */}
        <OnboardingActivation />

        {/* No home location → the lists below can only serve global content.
            Ask for the fix instead of failing silently; a saved home refetches
            the location-ranked lists immediately. */}
        <LocationNudgeCard
          required={dashboard.whatsOn.centerKind === 'none'}
          onHomeSet={() => {
            dashboard.retryWhatsOn();
            dashboard.retryWhatsAround();
            dashboard.retryFriends();
            dashboard.retryInspiration();
          }}
        />

        {/* This weekend near home — the only list carrying dated content.
            The wrapper is the lazy-load sentinel; it stays mounted (and
            hidden) when the section has nothing. */}
        <div ref={dashboard.whatsOnRef} className="empty:hidden">
          <ThisWeekendSection
            data={dashboard.whatsOn}
            centerKind={dashboard.whatsOn.centerKind}
            place={weekendPlace}
            awayFromHome={weekendAway}
            buildActions={buildActions}
            dismissedIds={dismissedIds}
            onRetry={dashboard.retryWhatsOn}
            sectionRef={weekendRef}
          />
        </div>

        {/* While you're in <place>: the destinations you've planned. */}
        <WhileAwaySection
          away={plans.away}
          buildActions={buildActions}
          onAdded={dashboard.refreshPlans}
          sectionRef={awayRef}
          focusTrip={awayFocus}
          suppressKeys={weekendShownKeys}
        />

        {/* Travel inspiration: people's posts, friends folded in. */}
        <div ref={dashboard.inspirationRef} className="empty:hidden">
          <TravelInspirationSection
            data={dashboard.inspiration}
            friends={dashboard.friends}
            buildActions={buildActions}
            modals={modals}
            dismissedIds={dismissedIds}
            onRetry={dashboard.retryInspiration}
            onRetryFriends={dashboard.retryFriends}
            friendsRef={dashboard.friendsRef}
          />
        </div>

        {/* New places around home — undated only. */}
        <div ref={dashboard.whatsAroundRef} className="empty:hidden">
          <NewPlacesSection
            data={dashboard.whatsAround}
            homeCity={homeCity}
            buildActions={buildActions}
            modals={modals}
            dismissedIds={dismissedIds}
            suppressKeys={beforeNewPlacesKeys}
            onRetry={dashboard.retryWhatsAround}
            attribution={googleBadge}
          />
        </div>
      </PageLayout>

      <InvitationResponseModal
        isOpen={!!liveInvite}
        onClose={() => setOpenInvite(null)}
        type={liveInvite?.type === 'trip' ? 'trip' : 'activity'}
        invitation={liveInvite ? toModalInvitation(liveInvite) : null}
        onRespond={respond}
      />

      {/* Feed Modals — shared across all sections, lazy-loaded on first open */}
      <Suspense fallback={null}>
        {modals.commentsModalOpen && (
          <CommentsModal
            isOpen={modals.commentsModalOpen}
            onClose={modals.handleCloseComments}
            contentType={modals.selectedItemForComments?.content_type}
            contentId={modals.selectedItemForComments?.id}
            contentTitle={modals.selectedItemForComments?.title}
            currentUserId={modals.currentUserId}
          />
        )}

        {modals.blockUserTarget && (
          <ConfirmDialog
            isOpen
            onClose={modals.cancelBlockUser}
            onConfirm={modals.confirmBlockUser}
            title={t('blockUserDialog.title')}
            message={t('blockUserDialog.message')}
            confirmText={t('blockUserDialog.confirmText')}
          />
        )}

        {modals.saveModalOpen && (
          <UnifiedSaveModal
            isOpen={modals.saveModalOpen}
            onClose={modals.handleCloseSaveModal}
            contentType={modals.selectedItemForSave?.content_type}
            contentId={modals.selectedItemForSave?.id}
            contentTitle={modals.selectedItemForSave?.title || modals.selectedItemForSave?.name}
            activity={modals.selectedItemForSave?.content_type === 'activity' ? modals.selectedItemForSave : null}
            foundVia={activityPostFoundVia(modals.selectedItemForSave)}
            onUpdate={modals.handleSaveUpdate}
          />
        )}

        {modals.showScheduleModal && (
          <ScheduleActivityModal
            isOpen={modals.showScheduleModal}
            onClose={() => {
              modals.setShowScheduleModal(false);
              modals.setActivityToSchedule(null);
            }}
            activity={modals.activityToSchedule}
            onScheduled={modals.handleScheduleComplete}
          />
        )}

        {modals.showInviteModal && (
          <ActivityInviteModal
            isOpen={modals.showInviteModal}
            onClose={() => {
              modals.setShowInviteModal(false);
              modals.setInviteActivityId(null);
              modals.setInviteActivityData(null);
            }}
            activityId={modals.inviteActivityId}
            activity={modals.inviteActivityData}
          />
        )}

        {modals.showNotInterestedModal && (
          <NotInterestedModal
            isOpen={modals.showNotInterestedModal}
            onClose={() => {
              modals.setShowNotInterestedModal(false);
              modals.setNotInterestedActivity(null);
            }}
            activity={modals.notInterestedActivity}
            onSubmit={modals.handleNotInterestedSubmit}
          />
        )}

        {modals.showSharingModal && (
          <SharingModal
            type={modals.sharingType}
            item={modals.sharingItem}
            isOpen={modals.showSharingModal}
            onClose={modals.handleCloseSharingModal}
            onUpdate={modals.handleCloseSharingModal}
          />
        )}

        {modals.showReportModal && (
          <ReportModal
            isOpen={modals.showReportModal}
            onClose={() => {
              modals.setShowReportModal(false);
              modals.setReportItem(null);
            }}
            item={modals.reportItem}
            onSubmit={modals.handleReportSubmit}
          />
        )}
      </Suspense>
    </div>
  );
}

export default Home;
